import { attachRowDailyHistoryRunLinks } from '@/features/shared/components/entityDailyHistory/dailyHistoryLinks';
import { buildDailyHistoryForReferenceDays } from '@/features/shared/components/entityDailyHistory/dailyHistoryRows';
import { resolveSuiteEntityDisplayName } from '@/features/suites/utils/suiteDisplayName';
import type { EntityDailyHistoryRow } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import type { SuiteDailyStatusDto } from '@flowqa/shared';
import type { SuiteListItemDto, TestRunDto } from '@flowqa/shared';

type SuiteHistoryExecutionDetail = {
  status: TestRunDto['status'];
  timestamp: Date;
  runLabel: string | null;
};

type SuiteHistoryTestInfo = {
  name: string;
  href: string;
  detailsByDay: Map<string, SuiteHistoryExecutionDetail[]>;
};

const buildSuiteHistoryTestName = (
  execution: TestRunDto,
  options?: { preferFileNameOverDisplayName?: boolean },
): string => {
  if (options?.preferFileNameOverDisplayName) {
    return execution.testName ?? execution.name ?? execution.testId;
  }
  const displayName =
    execution.displayName ?? execution.testName ?? execution.name ?? execution.testId;
  const nestedGroupPath = execution.groupPath?.slice(1) ?? [];
  if (nestedGroupPath.length === 0) {
    return displayName;
  }
  return `${nestedGroupPath.join(' › ')} › ${displayName}`;
};

const getSuiteTestCount = (suite: SuiteListItemDto): number => {
  const counts = suite.latestTestResults?.counts;
  if (!counts) {
    return 0;
  }
  return counts.passed + counts.failed + counts.errored + counts.skipped + counts.untested;
};

const hasSuiteTests = (suite: SuiteListItemDto): boolean => getSuiteTestCount(suite) > 0;

const getSuiteLoadingRowCount = (suite: SuiteListItemDto): number => {
  const testCount = getSuiteTestCount(suite);
  if (testCount <= 0) {
    return 2;
  }
  return Math.min(Math.max(testCount, 2), 6);
};

const collapseSuiteDailyHistoryToLatest = (
  dailyHistory: SuiteDailyStatusDto[] | undefined,
): SuiteDailyStatusDto[] =>
  (dailyHistory ?? []).map((historyDay) => {
    if (historyDay.details.length === 0) {
      return historyDay;
    }
    const latestDetail = historyDay.details[0];
    return {
      ...historyDay,
      status: latestDetail.status,
      statuses: [latestDetail.status],
      details: [latestDetail],
    };
  });

const filterSuiteDailyHistoryByStatuses = (
  dailyHistory: SuiteDailyStatusDto[] | undefined,
  selectedStatuses: Array<TestRunDto['status']>,
): SuiteDailyStatusDto[] => {
  if (selectedStatuses.length === 0) {
    return dailyHistory ?? [];
  }

  const selectedStatusSet = new Set(selectedStatuses);

  return (dailyHistory ?? []).map((historyDay) => {
    const details = historyDay.details.filter((detail) => selectedStatusSet.has(detail.status));
    const statuses = details.map((detail) => detail.status);

    return {
      ...historyDay,
      status: statuses[0] ?? null,
      statuses,
      details,
    };
  });
};

const buildSuiteChildRows = (
  suite: SuiteListItemDto,
  suiteTestRuns: TestRunDto[] | undefined,
  selectedStatuses: Array<TestRunDto['status']>,
  options?: { preferFileNameOverDisplayName?: boolean },
): EntityDailyHistoryRow[] => {
  const testInfoByTestId = new Map<string, SuiteHistoryTestInfo>();
  const selectedStatusSet = selectedStatuses.length > 0 ? new Set(selectedStatuses) : null;
  (suiteTestRuns ?? []).forEach((execution) => {
    if (selectedStatusSet !== null && !selectedStatusSet.has(execution.status)) {
      return;
    }

    const existing = testInfoByTestId.get(execution.testId) ?? {
      name: buildSuiteHistoryTestName(execution, options),
      href: execution.testSlug ? `/tests/${execution.testSlug}` : '/tests',
      detailsByDay: new Map<string, SuiteHistoryExecutionDetail[]>(),
    };
    const dayKey = execution.timestamp.toISOString().slice(0, 10);
    const details = existing.detailsByDay.get(dayKey) ?? [];
    details.push({
      status: execution.status,
      timestamp: execution.timestamp,
      runLabel:
        execution.runNumber !== null && execution.runNumber !== undefined
          ? `#${execution.runNumber}`
          : null,
    });
    existing.detailsByDay.set(dayKey, details);
    testInfoByTestId.set(execution.testId, existing);
  });

  return Array.from(testInfoByTestId.entries())
    .sort((left, right) =>
      left[1].name.localeCompare(right[1].name, undefined, { sensitivity: 'base' }),
    )
    .map(([testId, testInfo]) => ({
      id: `${suite.id}-${testId}`,
      name: testInfo.name,
      href: testInfo.href,
      dailyHistory: buildDailyHistoryForReferenceDays(
        suite.dailyHistory ?? [],
        testInfo.detailsByDay,
      ),
    }));
};

export const buildSuiteHistoryRows = (
  suites: SuiteListItemDto[] | undefined,
  suiteTestRunsBySuiteId: ReadonlyMap<string, TestRunDto[]>,
  expandedSuiteIds: ReadonlySet<string>,
  loadingSuiteIds: ReadonlySet<string>,
  selectedStatuses: Array<TestRunDto['status']> = [],
  latestOnlyMode = false,
  options?: { preferFileNameOverDisplayName?: boolean },
): EntityDailyHistoryRow[] =>
  (suites ?? []).map((suite) =>
    attachRowDailyHistoryRunLinks(
      {
        id: suite.id,
        name: resolveSuiteEntityDisplayName(suite, options),
        href: `/suites/${suite.slug}`,
        dailyHistory: latestOnlyMode
          ? collapseSuiteDailyHistoryToLatest(
              filterSuiteDailyHistoryByStatuses(suite.dailyHistory, selectedStatuses),
            )
          : filterSuiteDailyHistoryByStatuses(suite.dailyHistory, selectedStatuses),
        hasExpandableChildren: hasSuiteTests(suite),
        childrenLoading: loadingSuiteIds.has(suite.id),
        childrenLoadingRowCount: getSuiteLoadingRowCount(suite),
        childRows: expandedSuiteIds.has(suite.id)
          ? buildSuiteChildRows(
              suite,
              suiteTestRunsBySuiteId.get(suite.id),
              selectedStatuses,
              options,
            )
          : undefined,
      },
      'suite',
    ),
  );
